
import React, { useState } from 'react';
import PageTransition from '@/components/layout/PageTransition';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import AlertCard from '@/components/dashboard/AlertCard';
import { Server, Network, Bell, BellDot, ShieldAlert, Database, Computer, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Sample network alert data
const networkAlerts = [
  {
    id: 'NET001',
    type: 'critical',
    title: 'Node Container Offline',
    message: 'East Region Node Container (NC003) is not responding to health checks.',
    time: '10 minutes ago',
    location: 'East Region',
    affectedNodes: 5,
    source: 'Automated Health Monitor'
  },
  {
    id: 'NET002',
    type: 'warning',
    title: 'Network Latency Spike',
    message: 'Increased latency detected in West Region mesh network.',
    time: '25 minutes ago',
    location: 'West Region',
    affectedNodes: 12,
    source: 'Network Performance Monitor'
  },
  {
    id: 'NET003',
    type: 'warning',
    title: 'Bandwidth Utilization High',
    message: 'Central Node Container (NC001) experiencing 85% bandwidth utilization.',
    time: '45 minutes ago',
    location: 'Central Region',
    affectedNodes: 8,
    source: 'Resource Monitor'
  },
  {
    id: 'NET004',
    type: 'info',
    title: 'Routing Table Updated',
    message: 'Mesh network routing tables have been updated across all regions.',
    time: '1 hour ago',
    location: 'All Regions',
    affectedNodes: 25,
    source: 'Network Configuration System'
  }
];

// Sample device alert data
const deviceAlerts = [
  {
    id: 'DEV001',
    type: 'critical',
    title: 'Device Authentication Failed',
    message: 'Multiple failed authentication attempts for Kitchen Sensor (DEV002).',
    time: '5 minutes ago',
    deviceId: 'DEV002',
    deviceType: 'Temperature Sensor',
    location: 'Kitchen',
    assignedTo: 'Emma Smith'
  },
  {
    id: 'DEV002',
    type: 'critical',
    title: 'Storage Capacity Critical',
    message: 'Living Room Hub (DEV001) storage capacity at 98%.',
    time: '15 minutes ago',
    deviceId: 'DEV001',
    deviceType: 'Gateway',
    location: 'Living Room',
    assignedTo: 'John Doe'
  },
  {
    id: 'DEV003',
    type: 'warning',
    title: 'Battery Low',
    message: 'Bedroom Light (DEV004) battery level at 15%.',
    time: '30 minutes ago',
    deviceId: 'DEV004',
    deviceType: 'Light Controller',
    location: 'Bedroom',
    assignedTo: 'Alice Johnson'
  },
  {
    id: 'DEV004',
    type: 'info',
    title: 'Firmware Update Required',
    message: 'Outdoor Camera (DEV005) requires firmware update to version 2.3.4.',
    time: '2 hours ago',
    deviceId: 'DEV005',
    deviceType: 'Camera',
    location: 'Front Yard',
    assignedTo: 'Bob Wilson'
  }
];

const NetworkAlertSection = () => {
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');
  
  const filteredAlerts = networkAlerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.type === filter;
  });
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Network className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Network Alerts</h2>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={filter === 'all' ? 'default' : 'outline'} 
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            size="sm" 
            variant={filter === 'critical' ? 'default' : 'outline'} 
            onClick={() => setFilter('critical')}
            className="text-red-500"
          >
            Critical
          </Button>
          <Button 
            size="sm" 
            variant={filter === 'warning' ? 'default' : 'outline'} 
            onClick={() => setFilter('warning')}
            className="text-amber-500"
          >
            Warning
          </Button>
          <Button 
            size="sm" 
            variant={filter === 'info' ? 'default' : 'outline'} 
            onClick={() => setFilter('info')}
            className="text-blue-500"
          >
            Info
          </Button>
        </div>
      </div>
      
      {filteredAlerts.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          No alerts matching the selected filter.
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAlerts.map(alert => (
            <Card key={alert.id} className="overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-2/3 p-5">
                  <AlertCard
                    type={alert.type as 'critical' | 'warning' | 'info'}
                    title={alert.title}
                    message={alert.message}
                    time={alert.time}
                  />
                </div>
                <div className="md:w-1/3 bg-muted p-5 border-t md:border-t-0 md:border-l">
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-muted-foreground">Location:</span>
                      <p className="text-sm font-medium">{alert.location}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Affected Nodes:</span>
                      <p className="text-sm font-medium">{alert.affectedNodes}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Source:</span>
                      <p className="text-sm font-medium">{alert.source}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Alert ID:</span>
                      <p className="text-sm font-medium">{alert.id}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button variant="outline" size="sm" className="w-full">
                      Acknowledge
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const DeviceAlertSection = () => {
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');
  
  const filteredAlerts = deviceAlerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.type === filter;
  });
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Computer className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Device Alerts</h2>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={filter === 'all' ? 'default' : 'outline'} 
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            size="sm" 
            variant={filter === 'critical' ? 'default' : 'outline'} 
            onClick={() => setFilter('critical')}
            className="text-red-500"
          >
            Critical
          </Button>
          <Button 
            size="sm" 
            variant={filter === 'warning' ? 'default' : 'outline'} 
            onClick={() => setFilter('warning')}
            className="text-amber-500"
          >
            Warning
          </Button>
          <Button 
            size="sm" 
            variant={filter === 'info' ? 'default' : 'outline'} 
            onClick={() => setFilter('info')}
            className="text-blue-500"
          >
            Info
          </Button>
        </div>
      </div>
      
      {filteredAlerts.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          No alerts matching the selected filter.
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAlerts.map(alert => (
            <Card key={alert.id} className="overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-2/3 p-5">
                  <AlertCard
                    type={alert.type as 'critical' | 'warning' | 'info'}
                    title={alert.title}
                    message={alert.message}
                    time={alert.time}
                  />
                </div>
                <div className="md:w-1/3 bg-muted p-5 border-t md:border-t-0 md:border-l">
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-muted-foreground">Device ID:</span>
                      <p className="text-sm font-medium">{alert.deviceId}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Device Type:</span>
                      <p className="text-sm font-medium">{alert.deviceType}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Location:</span>
                      <p className="text-sm font-medium">{alert.location}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Assigned To:</span>
                      <p className="text-sm font-medium">{alert.assignedTo}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button variant="outline" size="sm" className="w-full">
                      Acknowledge
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const Alerts = () => {
  return (
    <PageTransition>
      <div className="section-padding">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Alert Management</h1>
          <p className="text-muted-foreground">
            Monitor and manage alerts across your mesh network
          </p>
        </div>
        
        <div className="mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BellDot className="h-5 w-5 text-red-500" />
                Alert Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <ShieldAlert className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="text-sm font-medium">Critical</p>
                    <p className="text-2xl font-bold">3</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <Bell className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium">Warning</p>
                    <p className="text-2xl font-bold">3</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <Info className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium">Info</p>
                    <p className="text-2xl font-bold">2</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg border">
                  <Database className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Total</p>
                    <p className="text-2xl font-bold">8</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="all" className="mb-8">
          <TabsList className="grid w-full grid-cols-3 md:w-auto md:inline-flex">
            <TabsTrigger value="all">All Alerts</TabsTrigger>
            <TabsTrigger value="network">Network</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-6 space-y-8">
            <NetworkAlertSection />
            <DeviceAlertSection />
          </TabsContent>
          
          <TabsContent value="network" className="mt-6">
            <NetworkAlertSection />
          </TabsContent>
          
          <TabsContent value="devices" className="mt-6">
            <DeviceAlertSection />
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

export default Alerts;
